import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProduct } from '../services/api';
import { FaStar, FaTruck, FaShieldAlt, FaUndo, FaHeart, FaShare, FaShoppingCart, FaArrowLeft, FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';

const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [activeTab, setActiveTab] = useState('description');

    useEffect(() => {
        fetchProduct();
        window.scrollTo(0, 0);
    }, [id]);

const addToCart = () => {
    const cart = JSON.parse(localStorage.getItem('tfm_cart') || '[]');
    const existingItem = cart.find(item => item.id === product.id);
    
    if (existingItem) {
        existingItem.quantity += quantity;
    } else {
        cart.push({ ...product, quantity: quantity });
    }
    
    localStorage.setItem('tfm_cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success(`${product.name} added to cart!`);
};

//     const addToCart = (product, quantity = 1) => {
//     const cart = JSON.parse(localStorage.getItem('tfm_cart') || '[]');
//     const existingItem = cart.find(item => item.id === product.id);
    
//     if (existingItem) {
//         existingItem.quantity += quantity;
//     } else {
//         cart.push({ ...product, quantity });
//     }
    
//     localStorage.setItem('tfm_cart', JSON.stringify(cart));
//     toast.success(`${product.name} added to cart!`);
// };


    const fetchProduct = async () => {
        try {
            const response = await getProduct(id);
            setProduct(response.product);
        } catch (error) {
            console.error('Error fetching product:', error);
            navigate('/products');
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = () => {
        toast.success(`Added ${quantity} ${product?.name} to cart!`, {
            position: "bottom-right",
            autoClose: 3000,
        });
    };

    const handleQuantityChange = (delta) => {
        const newQuantity = quantity + delta;
        if (newQuantity >= 1 && newQuantity <= (product?.stock_quantity || 10)) {
            setQuantity(newQuantity);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading product details...</p>
                </div>
            </div>
        );
    }

    if (!product) return null;

    const images = [product.image_url, ...(product.additional_images || [])].filter(Boolean);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Breadcrumb */}
            <div className="bg-white border-b">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Link to="/" className="hover:text-purple-600">Home</Link>
                        <span>/</span>
                        <Link to="/products" className="hover:text-purple-600">Products</Link>
                        <span>/</span>
                        <span className="text-gray-900">{product.name}</span>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                {/* Product Main Section */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 md:p-8">
                        {/* Product Images */}
                        <div>
                            <div className="relative h-96 md:h-[500px] rounded-xl overflow-hidden bg-gray-100 mb-4">
                                {images[selectedImage] ? (
                                    <img 
                                        src={images[selectedImage]} 
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                                        <span className="text-8xl">🧸</span>
                                    </div>
                                )}
                            </div>
                            {images.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {images.map((img, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSelectedImage(idx)}
                                            className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition ${selectedImage === idx ? 'border-purple-600' : 'border-transparent'}`}
                                        >
                                            <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Product Info */}
                        <div>
                            <div className="mb-4">
                                <span className="inline-block bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                                    {product.category}
                                </span>
                                <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">{product.name}</h1>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="flex text-yellow-400">
                                        {[...Array(5)].map((_, i) => (
                                            <FaStar key={i} className="text-lg" />
                                        ))}
                                    </div>
                                    <span className="text-gray-600">(128 customer reviews)</span>
                                </div>
                            </div>

                            <div className="mb-6">
                                <div className="text-3xl md:text-4xl font-bold text-purple-600 mb-2">
                                    £{product.price}
                                </div>
                                {product.old_price && (
                                    <div className="text-gray-400 line-through">£{product.old_price}</div>
                                )}
                            </div>

                            <div className="mb-6">
                                <p className="text-gray-600 leading-relaxed">{product.description}</p>
                            </div>

                            <div className="space-y-3 mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-5 h-5 bg-green-500 rounded-full"></div>
                                    <span className="text-gray-700">In Stock - Ready to Ship</span>
                                </div>
                                {product.age_range && (
                                    <div className="flex items-center gap-3">
                                        <span className="font-semibold text-gray-700">Age Range:</span>
                                        <span className="text-gray-600">{product.age_range}</span>
                                    </div>
                                )}
                                {product.sku && (
                                    <div className="flex items-center gap-3">
                                        <span className="font-semibold text-gray-700">SKU:</span>
                                        <span className="text-gray-600">{product.sku}</span>
                                    </div>
                                )}
                            </div>

                            {/* Quantity Selector */}
                            <div className="flex items-center gap-4 mb-6">
                                <span className="font-semibold text-gray-700">Quantity:</span>
                                <div className="flex items-center border border-gray-300 rounded-lg">
                                    <button
                                        onClick={() => handleQuantityChange(-1)}
                                        className="px-4 py-2 hover:bg-gray-100 transition"
                                    >
                                        -
                                    </button>
                                    <span className="px-6 py-2 border-x">{quantity}</span>
                                    <button
                                        onClick={() => handleQuantityChange(1)}
                                        className="px-4 py-2 hover:bg-gray-100 transition"
                                    >
                                        +
                                    </button>
                                </div>
                                <span className="text-sm text-gray-500">{product.stock_quantity} units available</span>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                              <button
    onClick={addToCart}
    disabled={product.stock_quantity === 0}
    className="flex-1 bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition transform hover:scale-105 flex items-center justify-center gap-2"
>
    <FaShoppingCart /> Add to Cart
</button>
                                <button className="px-6 py-3 border-2 border-purple-600 text-purple-600 rounded-xl font-semibold hover:bg-purple-50 transition flex items-center justify-center gap-2">
                                    <FaHeart /> Wishlist
                                </button>
                                <button className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition">
                                    <FaShare />
                                </button>
                            </div>

                            {/* Shipping Info */}
                            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                                <div className="flex items-center gap-3">
                                    <FaTruck className="text-purple-600 text-xl" />
                                    <div>
                                        <p className="font-semibold text-gray-800">Free Shipping</p>
                                        <p className="text-sm text-gray-600">On orders over £50</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaShieldAlt className="text-purple-600 text-xl" />
                                    <div>
                                        <p className="font-semibold text-gray-800">2-Year Warranty</p>
                                        <p className="text-sm text-gray-600">Manufacturer's warranty included</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaUndo className="text-purple-600 text-xl" />
                                    <div>
                                        <p className="font-semibold text-gray-800">30-Day Returns</p>
                                        <p className="text-sm text-gray-600">Easy returns within 30 days</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Product Details Tabs */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
                    <div className="border-b">
                        <div className="flex overflow-x-auto">
                            {['description', 'specifications', 'reviews'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-6 py-4 font-semibold transition relative ${activeTab === tab ? 'text-purple-600' : 'text-gray-600 hover:text-gray-800'}`}
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    {activeTab === tab && (
                                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600"></div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="p-6 md:p-8">
                        {activeTab === 'description' && (
                            <div className="prose max-w-none">
                                <h3 className="text-xl font-bold mb-4">Product Description</h3>
                                <p className="text-gray-600 leading-relaxed mb-4">{product.description}</p>
                                <p className="text-gray-600 leading-relaxed">
                                    {product.long_description || "This toy is designed to provide hours of entertainment while promoting cognitive development and creativity. Made from high-quality, non-toxic materials that are safe for children. Perfect for both individual play and group activities, encouraging social interaction and teamwork."}
                                </p>
                            </div>
                        )}
                        
                        {activeTab === 'specifications' && (
                            <div>
                                <h3 className="text-xl font-bold mb-4">Technical Specifications</h3>
                                <div className="space-y-3">
                                    <div className="flex py-2 border-b">
                                        <span className="w-32 font-semibold text-gray-700">Category</span>
                                        <span className="text-gray-600">{product.category}</span>
                                    </div>
                                    <div className="flex py-2 border-b">
                                        <span className="w-32 font-semibold text-gray-700">Age Range</span>
                                        <span className="text-gray-600">{product.age_range || '3+ years'}</span>
                                    </div>
                                    <div className="flex py-2 border-b">
                                        <span className="w-32 font-semibold text-gray-700">Material</span>
                                        <span className="text-gray-600">Non-toxic, BPA-free plastic</span>
                                    </div>
                                    <div className="flex py-2 border-b">
                                        <span className="w-32 font-semibold text-gray-700">Dimensions</span>
                                        <span className="text-gray-600">12 x 8 x 4 inches</span>
                                    </div>
                                    <div className="flex py-2 border-b">
                                        <span className="w-32 font-semibold text-gray-700">Weight</span>
                                        <span className="text-gray-600">1.5 lbs</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {activeTab === 'reviews' && (
                            <div>
                                <h3 className="text-xl font-bold mb-4">Customer Reviews</h3>
                                <div className="space-y-6">
                                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                                        <div className="text-center">
                                            <div className="text-4xl font-bold text-gray-800">4.8</div>
                                            <div className="flex text-yellow-400 mt-1">
                                                <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                                            </div>
                                            <div className="text-sm text-gray-600 mt-1">Based on 128 reviews</div>
                                        </div>
                                        <div className="flex-1 space-y-2">
                                            {[5,4,3,2,1].map(rating => (
                                                <div key={rating} className="flex items-center gap-2">
                                                    <span className="text-sm w-8">{rating}★</span>
                                                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                        <div className="h-full bg-yellow-400 rounded-full" style={{ width: `${rating === 5 ? 70 : rating === 4 ? 20 : 5}%` }}></div>
                                                    </div>
                                                    <span className="text-sm text-gray-600">{rating === 5 ? 90 : rating === 4 ? 25 : 13} reviews</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <button className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition">
                                        Write a Review
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Related Products Section - You can implement this later */}
                <div className="text-center py-8">
                    <Link to="/products" className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold">
                        <FaArrowLeft /> Back to Products
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ProductDetails;